import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
} from "@mui/x-data-grid-pro";
import { jaJPText } from "../stores/Mui_JapanesTxist";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import Grid from "@mui/material/Grid";
import { alpha, styled, ThemeProvider } from "@mui/material/styles";

import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

import axios from "axios";
import ConstList from "../utils/ConstList";

import SearchIcon from "@mui/icons-material/Search";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import LoginUserType from "../types/LoginUserType";

import theme from "../theme";

/* =========================================================
 型定義
========================================================= */

type ProjectType = "製造" | "IT" | "共通";
type ProjectCondition = "進行中" | "確認待ち" | "要対応" | "保留" | "完了";

type ProjectRow = {
  id: number;
  project_id: number;
  company_id: number;
  project_name: string;
  customer_name: string;
  project_type: ProjectType;
  condition: ProjectCondition;
  owner: string;
  members: string[];
  overview: string;
  current_phase: string;
  current_topic: string;
  next_action: string;
  next_action_date: string | null;
  last_activity_date: string | null;
  latest_activity: string;
  open_task_count: number;
  issue_count: number;
  activity_count: number;
  company_address: string;
  company_telephone: string;
  company_mail: string;
  company_website: string;
  is_attention_required: boolean;
  is_dormant: boolean;
};

type ProjectActivityRow = {
  id: number;
  activity_id: number;
  activity_date: string | null;
  author: string;
  activity_type: string;
  status: string;
  subject: string;
  content: string;
  next_action: string;
  decision: string;
  issue: string;
};

type ProjectFilter = "all" | "active" | "waiting" | "attention" | "dormant";
type ProjectDetailTab =
  | "overview"
  | "activity"
  | "tasks"
  | "issues"
  | "meetings"
  | "documents"
  | "history";

type LoginUserCompat = LoginUserType & {
  access_token?: string;
  accessToken?: string;
  AuthorityId?: number;
};

/* =========================================================
 色定義
========================================================= */

const pageColors = {
  pageBg: "#2D2C3A",
  panel: "#3A414C",
  panelDark: "#1F2530",
  panelDeep: "#1F1D30",
  panelSoft: "rgba(31,37,48,0.72)",
  accent: "#C5E763",
  cyan: "#00BFC5",
  blue: "#6DA8FF",
  text: "#FFFFFF",
  textSoft: "rgba(232,238,245,0.78)",
  textMuted: "#9FB0C0",
  border: "rgba(217,217,217,0.12)",
  borderStrong: "rgba(197,231,99,0.28)",
  warning: "#FFC107",
  error: "#FF8579",
  success: "#75D39A",
};

/* =========================================================
 共通UI
========================================================= */

const DashboardCard = styled(Card)(() => ({
  height: "100%",
  borderRadius: 14,
  backgroundColor: pageColors.panel,
  color: pageColors.text,
  boxShadow: "0 12px 28px rgba(0,0,0,0.20)",
  border: `1px solid ${pageColors.border}`,
  overflow: "hidden",
}));

const DetailPaper = styled(Paper)(() => ({
  padding: 14,
  borderRadius: 12,
  backgroundColor: pageColors.panelDark,
  border: `1px solid ${pageColors.border}`,
  color: pageColors.textSoft,
  height: "100%",
}));

/* =========================================================
 共通関数
========================================================= */

const toText = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const toNumber = (value: unknown, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? fallback : numberValue;
};

const toBoolean = (value: unknown) => {
  return (
    value === true ||
    value === "t" ||
    value === "true" ||
    value === 1 ||
    value === "1"
  );
};

const normalizeText = (text: string | number | null | undefined) => {
  if (text === null || text === undefined) return "";
  return String(text).normalize("NFKC").toLowerCase();
};

const normalizeUrl = (url: string | null | undefined) => {
  const value = String(url ?? "").trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `https://${value}`;
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return "-";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${year}/${month}/${day}`;
};

const getDaysFromToday = (date: string | null | undefined) => {
  if (!date) return null;

  const target = new Date(date);
  if (Number.isNaN(target.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diff = today.getTime() - target.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const parseApiData = (responseData: unknown) => {
  let parsed: any = responseData;

  try {
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
  } catch (error) {
    console.error("APIレスポンスのJSON変換に失敗しました。", error);
    throw new Error("APIレスポンスの形式が不正です。");
  }

  return parsed;
};

const inferProjectType = (source: string): ProjectType => {
  const normalized = normalizeText(source);

  const manufacturingWords = [
    "製造",
    "工場",
    "設備",
    "加工",
    "組立",
    "品質",
    "生産",
    "部品",
    "図面",
  ];
  const itWords = [
    "it",
    "システム",
    "アプリ",
    "web",
    "ソフト",
    "サーバ",
    "ネットワーク",
    "dx",
  ];

  const isManufacturing = manufacturingWords.some((word) =>
    normalized.includes(word),
  );
  const isIt = itWords.some((word) => normalized.includes(word));

  if (isManufacturing && isIt) return "共通";
  if (isManufacturing) return "製造";
  if (isIt) return "IT";
  return "共通";
};

const normalizeCondition = (
  source: unknown,
  attentionRequired: boolean,
  dormant: boolean,
): ProjectCondition => {
  const text = normalizeText(toText(source));

  if (
    text.includes("完了") ||
    text.includes("終了") ||
    text.includes("クローズ")
  ) {
    return "完了";
  }

  if (text.includes("保留") || text.includes("停止")) return "保留";
  if (text.includes("確認") || text.includes("承認") || text.includes("回答待ち")) {
    return "確認待ち";
  }
  if (attentionRequired || dormant || text.includes("要対応")) return "要対応";

  return "進行中";
};

const mapProjectRow = (
  value: any,
  index = 0,
  fallback?: ProjectRow,
): ProjectRow => {
  const companyId = toNumber(
    value?.company_id ?? fallback?.company_id,
    index + 1,
  );
  const projectId = toNumber(
    value?.project_id ?? value?.case_id ?? fallback?.project_id ?? companyId,
    index + 1,
  );

  const lastActivityDate =
    value?.last_activity_date ??
    value?.last_contact_date ??
    fallback?.last_activity_date ??
    null;
  const nextActionDate =
    value?.next_action_date ??
    value?.next_follow_date ??
    fallback?.next_action_date ??
    null;

  const daysFromLastActivity = getDaysFromToday(lastActivityDate);
  const nextActionOverDays = nextActionDate
    ? getDaysFromToday(nextActionDate)
    : null;

  const isAttentionRequired =
    value?.is_attention_required !== undefined
      ? toBoolean(value.is_attention_required)
      : value?.follow_required !== undefined
        ? toBoolean(value.follow_required)
        : (fallback?.is_attention_required ??
          (nextActionOverDays !== null
            ? nextActionOverDays >= 0
            : daysFromLastActivity !== null
              ? daysFromLastActivity >= 30
              : false));

  const isDormant =
    value?.is_dormant !== undefined
      ? toBoolean(value.is_dormant)
      : value?.dormant_candidate !== undefined
        ? toBoolean(value.dormant_candidate)
        : (fallback?.is_dormant ??
          (daysFromLastActivity !== null ? daysFromLastActivity >= 90 : false));

  const customerName = toText(
    value?.customer_name ?? value?.company_name ?? fallback?.customer_name,
  );
  const serviceName = toText(
    value?.project_name ??
      value?.case_name ??
      value?.contract_service ??
      value?.proposed_service ??
      fallback?.project_name,
  );
  const overview = toText(
    value?.overview ??
      value?.project_overview ??
      value?.business_description ??
      fallback?.overview,
  );
  const currentTopic = toText(
    value?.current_topic ??
      value?.latest_note ??
      value?.memo ??
      fallback?.current_topic,
  );
  const nextAction = toText(value?.next_action ?? fallback?.next_action);
  const projectType =
    value?.project_type === "製造" ||
    value?.project_type === "IT" ||
    value?.project_type === "共通"
      ? value.project_type
      : (fallback?.project_type ??
        inferProjectType(`${serviceName} ${overview} ${currentTopic}`));

  const condition = normalizeCondition(
    value?.condition ?? value?.project_status ?? value?.sales_status,
    isAttentionRequired,
    isDormant,
  );

  const rawMembers = value?.members ?? fallback?.members ?? [];
  const members = Array.isArray(rawMembers)
    ? rawMembers.map((member) => toText(member)).filter(Boolean)
    : toText(rawMembers)
        .split(/[、,]/)
        .map((member) => member.trim())
        .filter(Boolean);

  return {
    id: projectId,
    project_id: projectId,
    company_id: companyId,
    project_name:
      serviceName || (customerName ? `${customerName} 対応案件` : "名称未設定案件"),
    customer_name: customerName || "顧客未設定",
    project_type: projectType,
    condition,
    owner: toText(
      value?.owner ??
        value?.project_owner ??
        value?.sales_person ??
        fallback?.owner ??
        "未設定",
    ),
    members,
    overview,
    current_phase: toText(
      value?.current_phase ??
        value?.phase ??
        value?.sales_status ??
        fallback?.current_phase ??
        "対応中",
    ),
    current_topic: currentTopic,
    next_action: nextAction,
    next_action_date: nextActionDate,
    last_activity_date: lastActivityDate,
    latest_activity: toText(
      value?.latest_activity ?? value?.latest_note ?? fallback?.latest_activity,
    ),
    open_task_count: toNumber(
      value?.open_task_count ?? fallback?.open_task_count ?? (nextAction ? 1 : 0),
    ),
    issue_count: toNumber(
      value?.issue_count ??
        fallback?.issue_count ??
        (isAttentionRequired || isDormant ? 1 : 0),
    ),
    activity_count: toNumber(
      value?.activity_count ??
        value?.daily_report_count ??
        value?.sales_history_count ??
        fallback?.activity_count,
    ),
    company_address: toText(
      value?.company_address ?? fallback?.company_address,
    ),
    company_telephone: toText(
      value?.company_telephone ?? fallback?.company_telephone,
    ),
    company_mail: toText(value?.company_mail ?? fallback?.company_mail),
    company_website: toText(
      value?.company_website ?? fallback?.company_website,
    ),
    is_attention_required: isAttentionRequired,
    is_dormant: isDormant,
  };
};

const mapActivityRows = (rawHistories: unknown): ProjectActivityRow[] => {
  if (!Array.isArray(rawHistories)) return [];

  return rawHistories.map((value: any, index: number) => {
    const content = toText(
      value?.content ??
        value?.daily_report ??
        value?.memo ??
        value?.sales_review ??
        value?.proposal_details,
    );

    return {
      id: toNumber(
        value?.activity_id ?? value?.daily_report_id ?? value?.sales_record_id,
        index + 1,
      ),
      activity_id: toNumber(
        value?.activity_id ?? value?.daily_report_id ?? value?.sales_record_id,
        index + 1,
      ),
      activity_date:
        value?.activity_date ?? value?.report_date ?? value?.sales_date ?? null,
      author: toText(
        value?.author ?? value?.reporter ?? value?.sales_person ?? "担当者未設定",
      ),
      activity_type: toText(
        value?.activity_type ??
          value?.report_type ??
          value?.sales_approach_method ??
          "活動記録",
      ),
      status: toText(value?.status ?? value?.sales_status),
      subject: toText(value?.subject ?? value?.title ?? value?.proposed_service),
      content,
      next_action: toText(value?.next_action),
      decision: toText(value?.decision ?? value?.decision_details),
      issue: toText(value?.issue ?? value?.problem ?? value?.purchase_intention),
    };
  });
};

const getConditionColor = (condition: ProjectCondition) => {
  switch (condition) {
    case "進行中":
      return pageColors.accent;
    case "確認待ち":
      return pageColors.cyan;
    case "要対応":
      return pageColors.warning;
    case "保留":
      return pageColors.textMuted;
    case "完了":
      return pageColors.success;
    default:
      return pageColors.textSoft;
  }
};

const getProjectTypeIcon = (projectType: ProjectType) => {
  if (projectType === "製造") return <FactoryOutlinedIcon sx={{ fontSize: 17 }} />;
  if (projectType === "IT") return <ComputerOutlinedIcon sx={{ fontSize: 17 }} />;
  return <HubOutlinedIcon sx={{ fontSize: 17 }} />;
};

/* =========================================================
 メインコンポーネント
========================================================= */

type ProjectManagementDataProps = {
  embedded?: boolean;
};

const ProjectManagementData = ({ embedded = false }: ProjectManagementDataProps) => {
  const loginUserData = useRecoilValue(LoginUserAtom) as LoginUserCompat;

  const accessToken =
    loginUserData?.access_token ?? loginUserData?.accessToken ?? "";

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [detailErrorMessage, setDetailErrorMessage] = useState<string | null>(
    null,
  );

  const [projectData, setProjectData] = useState<ProjectRow[]>([]);
  const [activities, setActivities] = useState<ProjectActivityRow[]>([]);

  const [searchText, setSearchText] = useState("");
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>("all");
  const [selectedRow, setSelectedRow] = useState<ProjectRow | null>(null);
  const [selectedTab, setSelectedTab] = useState<ProjectDetailTab>("overview");

  const gridSx = {
    border: "none",
    color: pageColors.text,
    backgroundColor: pageColors.panelDark,
    overflow: "hidden",

    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: pageColors.panelDeep,
      color: pageColors.text,
      borderBottom: `1px solid ${pageColors.border}`,
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: pageColors.panelDeep,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 800,
      color: pageColors.text,
    },
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${pageColors.border}`,
      color: pageColors.textSoft,
      outline: "none !important",
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "rgba(197,231,99,0.06)",
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${pageColors.border}`,
      backgroundColor: pageColors.panelDeep,
      color: pageColors.textSoft,
    },
    "& .MuiTablePagination-root": {
      color: pageColors.textSoft,
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      color: pageColors.textSoft,
    },
    "& .MuiSvgIcon-root": {
      color: pageColors.textSoft,
    },
    "& .MuiDataGrid-overlay": {
      backgroundColor: pageColors.panelDark,
      color: pageColors.textSoft,
    },
  };

  /* =========================================================
   データ取得
   現行APIとの互換性を残し、project_management が未実装の場合は
   existing_customers のデータも案件表示へ変換できるようにしています。
  ========================================================= */

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    const postData = {
      flag: "existing_customers",
      access_token: accessToken,
      algorithm: loginUserData?.algorithm,
      loginUserAuthority: loginUserData?.AuthorityId,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "ExistingCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG,
      );

      const apiData = parseApiData(response.data);

      if (
        apiData?.data === false ||
        (apiData?.status !== undefined && Number(apiData.status) !== 1)
      ) {
        setErrorMessage(apiData?.message ?? "案件データの取得に失敗しました。");

        if (apiData?.reDirect) {
          await ConstList.SLEEP(5000);
          window.location.href = apiData.reDirect;
        }

        return;
      }

      const rawList = Array.isArray(apiData?.data?.projects)
        ? apiData.data.projects
        : Array.isArray(apiData?.data?.existing_customers)
          ? apiData.data.existing_customers
          : [];

      setProjectData(
        rawList.map((value: any, index: number) =>
          mapProjectRow(value, index),
        ),
      );
    } catch (error) {
      console.error("案件データ取得エラー:", error);
      setErrorMessage("案件データの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [accessToken, loginUserData?.algorithm, loginUserData?.AuthorityId]);

  const fetchDetail = async (row: ProjectRow) => {
    setSelectedRow(row);
    setSelectedTab("overview");
    setActivities([]);
    setDetailErrorMessage(null);
    setDetailLoading(true);

    const postData = {
      flag: "existing_customer_detail",
      company_id: row.company_id,
      project_id: row.project_id,
      access_token: accessToken,
      algorithm: loginUserData?.algorithm,
      loginUserAuthority: loginUserData?.AuthorityId,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "ExistingCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG,
      );

      const apiData = parseApiData(response.data);

      if (
        apiData?.data === false ||
        (apiData?.status !== undefined && Number(apiData.status) !== 1)
      ) {
        setDetailErrorMessage(
          apiData?.message ?? "案件詳細の取得に失敗しました。",
        );
        return;
      }

      const payload = apiData?.data ?? {};
      const rawProject = payload?.project ?? payload?.customer;

      if (rawProject && typeof rawProject === "object") {
        setSelectedRow(mapProjectRow(rawProject, 0, row));
      }

      const rawActivities =
        payload?.activities ?? payload?.daily_reports ?? payload?.sales_history ?? [];

      setActivities(mapActivityRows(rawActivities));
    } catch (error) {
      console.error("案件詳細取得エラー:", error);
      setDetailErrorMessage("案件詳細の取得に失敗しました。");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken || !loginUserData?.algorithm) return;
    fetchData();
  }, [accessToken, fetchData, loginUserData?.algorithm]);

  /* =========================================================
   検索・フィルター
  ========================================================= */

  const filteredData = useMemo(() => {
    const keyword = normalizeText(searchText);

    return projectData.filter((row) => {
      const hitKeyword =
        !keyword ||
        normalizeText(
          [
            row.project_name,
            row.customer_name,
            row.project_type,
            row.condition,
            row.owner,
            row.members.join(" "),
            row.overview,
            row.current_phase,
            row.current_topic,
            row.next_action,
            row.latest_activity,
          ].join(" "),
        ).includes(keyword);

      if (!hitKeyword) return false;

      if (projectFilter === "active") return row.condition === "進行中";
      if (projectFilter === "waiting") return row.condition === "確認待ち";
      if (projectFilter === "attention") return row.is_attention_required;
      if (projectFilter === "dormant") return row.is_dormant;

      return true;
    });
  }, [projectData, projectFilter, searchText]);

  const operationalFilters = useMemo(
    () => [
      {
        key: "all" as ProjectFilter,
        label: "すべての案件",
        description: "担当案件をまとめて確認",
        value: projectData.length,
        icon: <FolderOutlinedIcon />,
        color: pageColors.accent,
      },
      {
        key: "active" as ProjectFilter,
        label: "進行中",
        description: "現在対応を進めている案件",
        value: projectData.filter((row) => row.condition === "進行中").length,
        icon: <TaskAltOutlinedIcon />,
        color: pageColors.cyan,
      },
      {
        key: "waiting" as ProjectFilter,
        label: "確認待ち",
        description: "顧客・社内の回答待ち",
        value: projectData.filter((row) => row.condition === "確認待ち").length,
        icon: <PendingActionsOutlinedIcon />,
        color: pageColors.blue,
      },
      {
        key: "attention" as ProjectFilter,
        label: "要確認",
        description: "対応期限・更新状況を確認",
        value: projectData.filter((row) => row.is_attention_required).length,
        icon: <WarningAmberOutlinedIcon />,
        color: pageColors.warning,
      },
    ],
    [projectData],
  );

  /* =========================================================
   DataGrid カラム
  ========================================================= */

  const columns: GridColDef<ProjectRow>[] = [
    {
      field: "project_name",
      headerName: "案件",
      minWidth: 260,
      flex: 1.25,
      renderCell: (params) => (
        <Stack spacing={0.35} sx={{ minWidth: 0, width: "100%" }}>
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              fontWeight={900}
              sx={{
                color: pageColors.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={params.row.project_name}
            >
              {params.row.project_name}
            </Typography>

            {params.row.issue_count > 0 && (
              <Chip
                size="small"
                icon={<WarningAmberOutlinedIcon />}
                label="課題あり"
                sx={{
                  height: 21,
                  color: pageColors.warning,
                  backgroundColor: "rgba(255,193,7,0.12)",
                  border: "1px solid rgba(255,193,7,0.28)",
                  fontSize: 11,
                  fontWeight: 800,
                  flexShrink: 0,
                  "& .MuiChip-icon": { color: pageColors.warning, fontSize: 14 },
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ color: pageColors.textMuted, display: "flex" }}>
              {getProjectTypeIcon(params.row.project_type)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: pageColors.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={params.row.customer_name}
            >
              {params.row.customer_name} ・ {params.row.project_type}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      field: "condition",
      headerName: "状況",
      width: 118,
      renderCell: (params) => {
        const color = getConditionColor(params.row.condition);
        return (
          <Chip
            size="small"
            label={params.row.condition}
            sx={{
              color,
              backgroundColor: alpha(color, 0.12),
              border: `1px solid ${alpha(color, 0.3)}`,
              fontWeight: 900,
            }}
          />
        );
      },
    },
    {
      field: "owner",
      headerName: "担当",
      width: 128,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
          <PersonOutlineRoundedIcon
            sx={{ fontSize: 18, color: pageColors.textMuted, flexShrink: 0 }}
          />
          <Typography
            sx={{
              color: pageColors.textSoft,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.owner}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "last_activity_date",
      headerName: "最終更新",
      width: 130,
      renderCell: (params) => {
        const days = getDaysFromToday(params.row.last_activity_date);
        return (
          <Stack spacing={0.1}>
            <Typography sx={{ color: pageColors.textSoft, fontWeight: 700 }}>
              {formatDate(params.row.last_activity_date)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color:
                  days !== null && days >= 30
                    ? pageColors.warning
                    : pageColors.textMuted,
                fontWeight: days !== null && days >= 30 ? 900 : 500,
              }}
            >
              {days === null ? "記録なし" : days === 0 ? "本日" : `${days}日前`}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "current_topic",
      headerName: "現在の論点・状況",
      minWidth: 250,
      flex: 1.15,
      renderCell: (params) => (
        <Tooltip title={params.row.current_topic || "記録なし"}>
          <Typography
            sx={{
              color: params.row.current_topic
                ? pageColors.textSoft
                : pageColors.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {params.row.current_topic || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "next_action",
      headerName: "次の対応",
      minWidth: 240,
      flex: 1.05,
      renderCell: (params) => (
        <Stack spacing={0.15} sx={{ width: "100%", minWidth: 0 }}>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
            <ArrowForwardRoundedIcon
              sx={{ fontSize: 18, color: pageColors.accent, flexShrink: 0 }}
            />
            <Typography
              sx={{
                color: params.row.next_action
                  ? pageColors.textSoft
                  : pageColors.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={params.row.next_action}
            >
              {params.row.next_action || "未設定"}
            </Typography>
          </Stack>
          {params.row.next_action_date && (
            <Typography variant="caption" sx={{ color: pageColors.textMuted, pl: 3 }}>
              {formatDate(params.row.next_action_date)}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "activity_count",
      headerName: "記録",
      width: 86,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack alignItems="center" spacing={0.1}>
          <EventNoteOutlinedIcon sx={{ fontSize: 18, color: pageColors.textMuted }} />
          <Typography variant="caption" sx={{ color: pageColors.textMuted, fontWeight: 700 }}>
            {params.row.activity_count}件
          </Typography>
        </Stack>
      ),
    },
    {
      field: "action",
      headerName: "詳細",
      width: 86,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="案件詳細を表示">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              fetchDetail(params.row);
            }}
            sx={{
              color: pageColors.accent,
              border: `1px solid ${pageColors.borderStrong}`,
              backgroundColor: "rgba(197,231,99,0.08)",
              "&:hover": { backgroundColor: "rgba(197,231,99,0.16)" },
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const handleRowDoubleClick = (params: GridRowParams<ProjectRow>) => {
    fetchDetail(params.row);
  };

  const closeDialog = () => {
    setSelectedRow(null);
    setActivities([]);
    setDetailErrorMessage(null);
    setSelectedTab("overview");
  };

  return (
    <ThemeProvider theme={theme}>
      {!embedded && (
        <GlobalStyles
          styles={{
            html: { overflowY: "hidden" },
            body: { overflowY: "hidden" },
            "#root": { overflowY: "hidden" },
          }}
        />
      )}

      <Box
        sx={{
          height: embedded
            ? "100%"
            : { xs: "calc(100dvh - 96px)", md: "calc(100dvh - 150px)" },
          maxHeight: embedded
            ? "100%"
            : { xs: "calc(100dvh - 96px)", md: "calc(100dvh - 150px)" },
          minHeight: 0,
          overflow: "hidden",
          background: embedded
            ? "transparent"
            : "radial-gradient(circle at top left, rgba(197,231,99,0.10), transparent 28%), #2D2C3A",
          p: embedded ? 0 : { xs: 1.25, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,
              p: { xs: 1.4, md: 1.7 },
              borderRadius: 3,
              backgroundColor: pageColors.panelSoft,
              border: `1px solid ${pageColors.border}`,
              boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FolderOutlinedIcon sx={{ color: pageColors.accent }} />
                  <Typography
                    variant="h5"
                    fontWeight={900}
                    sx={{ color: pageColors.text, letterSpacing: "0.03em" }}
                  >
                    案件管理
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  sx={{ color: pageColors.textSoft, mt: 0.45, lineHeight: 1.6 }}
                >
                  製造・IT案件の現在状況、日報、課題、決定事項を案件単位で確認します。
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
                <TextField
                  label="案件名・顧客・担当・内容で検索"
                  variant="outlined"
                  size="small"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: pageColors.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", md: 350 },
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: pageColors.panelDark,
                      "&:hover": { backgroundColor: "#303746" },
                      "&.Mui-focused": {
                        backgroundColor: "#222838",
                        boxShadow: "0 0 0 2px rgba(197,231,99,0.15)",
                      },
                    },
                    "& .MuiInputBase-input": { color: pageColors.text },
                    "& .MuiInputLabel-root": { color: pageColors.textMuted },
                    "& .MuiInputLabel-root.Mui-focused": { color: pageColors.textMuted },
                  }}
                />

                <Button
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={fetchData}
                  disabled={loading}
                  sx={{
                    color: pageColors.textSoft,
                    borderColor: pageColors.border,
                    fontWeight: 800,
                    "&:hover": {
                      borderColor: pageColors.accent,
                      backgroundColor: "rgba(197,231,99,0.08)",
                    },
                  }}
                >
                  再取得
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ flexShrink: 0 }}>
              {errorMessage}
            </Alert>
          )}

          <Grid container spacing={1.2} sx={{ flexShrink: 0 }}>
            {operationalFilters.map((item) => {
              const selected = projectFilter === item.key;

              return (
                <Grid key={item.key} size={{ xs: 12, sm: 6, md: 3 }}>
                  <DashboardCard
                    onClick={() => setProjectFilter(item.key)}
                    sx={{
                      cursor: "pointer",
                      border: selected
                        ? `1px solid ${item.color}`
                        : `1px solid ${pageColors.border}`,
                      backgroundColor: selected
                        ? alpha(item.color, 0.1)
                        : pageColors.panel,
                    }}
                  >
                    <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            color: item.color,
                            backgroundColor: alpha(item.color, 0.12),
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={0.8} alignItems="baseline">
                            <Typography fontWeight={900} sx={{ color: pageColors.text }}>
                              {item.label}
                            </Typography>
                            <Typography fontWeight={900} sx={{ color: item.color }}>
                              {item.value}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="caption"
                            sx={{
                              color: pageColors.textMuted,
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </DashboardCard>
                </Grid>
              );
            })}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              backgroundColor: pageColors.panel,
              border: `1px solid ${pageColors.border}`,
              boxShadow: "0 12px 28px rgba(0,0,0,0.20)",
              overflow: "hidden",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={1.1}
              sx={{
                flexShrink: 0,
                px: { xs: 1.5, md: 2 },
                py: 1.15,
                borderBottom: `1px solid ${pageColors.border}`,
                backgroundColor: pageColors.panelDeep,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AssignmentOutlinedIcon sx={{ color: pageColors.accent }} />
                <Typography fontWeight={900} sx={{ color: pageColors.text }}>
                  案件一覧
                </Typography>
                <Chip
                  size="small"
                  label={`${filteredData.length}件`}
                  sx={{
                    color: pageColors.accent,
                    backgroundColor: "rgba(197,231,99,0.12)",
                    border: `1px solid ${pageColors.borderStrong}`,
                    fontWeight: 800,
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={0.7} sx={{ overflowX: "auto" }}>
                {[
                  { key: "all" as ProjectFilter, label: "すべて" },
                  { key: "active" as ProjectFilter, label: "進行中" },
                  { key: "waiting" as ProjectFilter, label: "確認待ち" },
                  { key: "attention" as ProjectFilter, label: "要確認" },
                  { key: "dormant" as ProjectFilter, label: "長期未更新" },
                ].map((item) => (
                  <Chip
                    key={item.key}
                    size="small"
                    clickable
                    label={item.label}
                    onClick={() => setProjectFilter(item.key)}
                    sx={{
                      height: 25,
                      flexShrink: 0,
                      color:
                        projectFilter === item.key
                          ? pageColors.accent
                          : pageColors.textSoft,
                      backgroundColor:
                        projectFilter === item.key
                          ? "rgba(197,231,99,0.13)"
                          : "rgba(255,255,255,0.05)",
                      border:
                        projectFilter === item.key
                          ? `1px solid ${pageColors.borderStrong}`
                          : `1px solid ${pageColors.border}`,
                      fontWeight: 800,
                    }}
                  />
                ))}
              </Stack>
            </Stack>

            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: pageColors.textSoft,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, width: "100%", overflow: "hidden" }}>
                <DataGridPro<ProjectRow>
                  rows={filteredData}
                  columns={columns}
                  sx={{ ...gridSx, height: "100%" }}
                  localeText={jaJPText}
                  loading={loading}
                  rowHeight={70}
                  autoPageSize
                  disableRowSelectionOnClick
                  onRowDoubleClick={handleRowDoubleClick}
                  initialState={{
                    sorting: {
                      sortModel: [
                        { field: "last_activity_date", sort: "desc" },
                        { field: "condition", sort: "asc" },
                      ],
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Stack>

        <Dialog
          open={Boolean(selectedRow)}
          onClose={closeDialog}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              height: { xs: "92dvh", md: "88dvh" },
              backgroundColor: pageColors.pageBg,
              color: pageColors.text,
              border: `1px solid ${pageColors.border}`,
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
        >
          {selectedRow && (
            <>
              <DialogTitle sx={{ pb: 1.2 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                  spacing={1.2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Typography
                        variant="h6"
                        fontWeight={900}
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {selectedRow.project_name}
                      </Typography>
                      <Chip
                        size="small"
                        label={selectedRow.condition}
                        sx={{
                          color: getConditionColor(selectedRow.condition),
                          backgroundColor: alpha(
                            getConditionColor(selectedRow.condition),
                            0.12,
                          ),
                          border: `1px solid ${alpha(
                            getConditionColor(selectedRow.condition),
                            0.3,
                          )}`,
                          fontWeight: 900,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" sx={{ color: pageColors.textMuted, mt: 0.35 }}>
                      {selectedRow.customer_name} ・ {selectedRow.project_type} ・ 担当 {selectedRow.owner}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<EventNoteOutlinedIcon />}
                      label={`活動記録 ${selectedRow.activity_count}件`}
                      size="small"
                      sx={{
                        color: pageColors.textSoft,
                        backgroundColor: "rgba(255,255,255,0.06)",
                        border: `1px solid ${pageColors.border}`,
                        fontWeight: 800,
                      }}
                    />
                    {selectedRow.issue_count > 0 && (
                      <Chip
                        icon={<WarningAmberOutlinedIcon />}
                        label={`未解決課題 ${selectedRow.issue_count}件`}
                        size="small"
                        sx={{
                          color: pageColors.warning,
                          backgroundColor: "rgba(255,193,7,0.10)",
                          border: "1px solid rgba(255,193,7,0.28)",
                          fontWeight: 800,
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </DialogTitle>

              <Box sx={{ px: 2, borderBottom: `1px solid ${pageColors.border}` }}>
                <Tabs
                  value={selectedTab}
                  onChange={(_, value: ProjectDetailTab) => setSelectedTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      minHeight: 44,
                      color: pageColors.textMuted,
                      fontWeight: 800,
                      textTransform: "none",
                    },
                    "& .Mui-selected": { color: `${pageColors.accent} !important` },
                    "& .MuiTabs-indicator": { backgroundColor: pageColors.accent },
                  }}
                >
                  <Tab value="overview" icon={<AssignmentOutlinedIcon />} iconPosition="start" label="概要" />
                  <Tab value="activity" icon={<EventNoteOutlinedIcon />} iconPosition="start" label="日報・活動記録" />
                  <Tab value="tasks" icon={<TaskAltOutlinedIcon />} iconPosition="start" label="タスク" />
                  <Tab value="issues" icon={<WarningAmberOutlinedIcon />} iconPosition="start" label="課題・不具合" />
                  <Tab value="meetings" icon={<GroupsOutlinedIcon />} iconPosition="start" label="打合せ・決定事項" />
                  <Tab value="documents" icon={<DescriptionOutlinedIcon />} iconPosition="start" label="資料" />
                  <Tab value="history" icon={<HistoryOutlinedIcon />} iconPosition="start" label="履歴" />
                </Tabs>
              </Box>

              <DialogContent sx={{ p: { xs: 1.5, md: 2 }, overflowY: "auto" }}>
                {detailErrorMessage && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {detailErrorMessage}
                  </Alert>
                )}

                {selectedTab === "overview" && <OverviewTab project={selectedRow} />}
                {selectedTab === "activity" && (
                  <ActivityTab loading={detailLoading} activities={activities} />
                )}
                {selectedTab === "tasks" && (
                  <TasksTab project={selectedRow} activities={activities} loading={detailLoading} />
                )}
                {selectedTab === "issues" && (
                  <IssuesTab project={selectedRow} activities={activities} loading={detailLoading} />
                )}
                {selectedTab === "meetings" && (
                  <MeetingsTab activities={activities} loading={detailLoading} />
                )}
                {selectedTab === "documents" && <DocumentsTab project={selectedRow} />}
                {selectedTab === "history" && (
                  <HistoryTab activities={activities} loading={detailLoading} />
                )}
              </DialogContent>

              <DialogActions sx={{ borderTop: `1px solid ${pageColors.border}`, px: 2 }}>
                <Button variant="contained" onClick={closeDialog}>
                  閉じる
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

/* =========================================================
 詳細タブ
========================================================= */

type ProjectTabProps = {
  project: ProjectRow;
};

const OverviewTab = ({ project }: ProjectTabProps) => {
  return (
    <Stack spacing={1.6}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          backgroundColor: pageColors.panel,
          border: `1px solid ${pageColors.border}`,
        }}
      >
        <Typography variant="caption" sx={{ color: pageColors.textMuted, fontWeight: 800 }}>
          この案件の現在地
        </Typography>
        <Typography sx={{ mt: 0.5, color: pageColors.text, fontWeight: 800, lineHeight: 1.8 }}>
          {project.current_topic || project.latest_activity || "現在の状況がまだ記録されていません。"}
        </Typography>
      </Paper>

      <Grid container spacing={1.3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailBlock label="現在の段階" value={project.current_phase || "未設定"} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailBlock label="担当者" value={project.owner} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailBlock label="最終更新" value={formatDate(project.last_activity_date)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailBlock label="案件区分" value={project.project_type} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailBlock label="案件概要・目的" value={project.overview || "未登録"} multiline />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailBlock
            label="次の対応"
            value={project.next_action || "次の対応は未設定です。"}
            subValue={project.next_action_date ? `予定日 ${formatDate(project.next_action_date)}` : undefined}
            multiline
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          backgroundColor: pageColors.panel,
          border: `1px solid ${pageColors.border}`,
        }}
      >
        <Typography fontWeight={900} sx={{ mb: 1, color: pageColors.text }}>
          関係者・顧客情報
        </Typography>
        <Grid container spacing={1.2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailBlock label="顧客名" value={project.customer_name} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailBlock
              label="案件メンバー"
              value={project.members.length > 0 ? project.members.join("、") : "未設定"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailBlock label="電話番号" value={project.company_telephone || "未登録"} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailBlock label="メール" value={project.company_mail || "未登録"} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <DetailBlock label="住所" value={project.company_address || "未登録"} />
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );
};

type ActivityTabProps = {
  activities: ProjectActivityRow[];
  loading: boolean;
};

const ActivityTab = ({ activities, loading }: ActivityTabProps) => {
  if (loading) return <DetailLoading label="活動記録を読み込み中..." />;

  if (activities.length === 0) {
    return (
      <EmptyState
        icon={<EventNoteOutlinedIcon />}
        title="日報・活動記録がありません"
        description="作業内容、顧客とのやり取り、発生した問題、次に行うことを案件へ残します。"
      />
    );
  }

  return (
    <Stack spacing={1}>
      {activities.map((activity, index) => (
        <ActivityCard key={`${activity.activity_id}-${index}`} activity={activity} index={index} />
      ))}
    </Stack>
  );
};

type TasksTabProps = ActivityTabProps & {
  project: ProjectRow;
};

const TasksTab = ({ project, activities, loading }: TasksTabProps) => {
  if (loading) return <DetailLoading label="タスクを読み込み中..." />;

  const tasks = [
    ...(project.next_action
      ? [
          {
            id: `current-${project.project_id}`,
            content: project.next_action,
            date: project.next_action_date,
            owner: project.owner,
            status: "未完了",
          },
        ]
      : []),
    ...activities
      .filter((activity) => activity.next_action)
      .map((activity) => ({
        id: `activity-${activity.activity_id}`,
        content: activity.next_action,
        date: null,
        owner: activity.author,
        status: "記録済み",
      })),
  ];

  if (tasks.length === 0) {
    return (
      <EmptyState
        icon={<TaskAltOutlinedIcon />}
        title="タスクが登録されていません"
        description="誰が、何を、いつまでに行うかを案件単位で管理します。"
      />
    );
  }

  return (
    <Stack spacing={1}>
      {tasks.map((task) => (
        <Paper
          key={task.id}
          elevation={0}
          sx={{
            p: 1.4,
            borderRadius: 2,
            backgroundColor: pageColors.panelDark,
            border: `1px solid ${pageColors.border}`,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <CheckCircleOutlineRoundedIcon sx={{ color: pageColors.accent, mt: 0.2 }} />
              <Box>
                <Typography sx={{ color: pageColors.text, fontWeight: 800, lineHeight: 1.7 }}>
                  {task.content}
                </Typography>
                <Typography variant="caption" sx={{ color: pageColors.textMuted }}>
                  担当 {task.owner}
                  {task.date ? ` ・ 期限 ${formatDate(task.date)}` : ""}
                </Typography>
              </Box>
            </Stack>
            <Chip
              size="small"
              label={task.status}
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                color: pageColors.cyan,
                backgroundColor: "rgba(0,191,197,0.10)",
                border: "1px solid rgba(0,191,197,0.26)",
                fontWeight: 800,
              }}
            />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const IssuesTab = ({ project, activities, loading }: TasksTabProps) => {
  if (loading) return <DetailLoading label="課題・不具合を読み込み中..." />;

  const activityIssues = activities.filter((activity) => activity.issue);
  const hasProjectIssue = project.issue_count > 0 || project.is_attention_required;

  if (!hasProjectIssue && activityIssues.length === 0) {
    return (
      <EmptyState
        icon={<WarningAmberOutlinedIcon />}
        title="未解決の課題はありません"
        description="製造不具合、システム障害、仕様上の懸念、確認事項を案件へ紐づけます。"
      />
    );
  }

  return (
    <Stack spacing={1}>
      {hasProjectIssue && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "rgba(255,193,7,0.08)",
            border: "1px solid rgba(255,193,7,0.26)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <WarningAmberOutlinedIcon sx={{ color: pageColors.warning, mt: 0.2 }} />
            <Box>
              <Typography sx={{ color: pageColors.text, fontWeight: 900 }}>
                現在の確認事項
              </Typography>
              <Typography sx={{ color: pageColors.textSoft, mt: 0.45, lineHeight: 1.7 }}>
                {project.current_topic || "長期間更新されていないため、案件状況の確認が必要です。"}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {activityIssues.map((activity) => (
        <Paper
          key={activity.activity_id}
          elevation={0}
          sx={{
            p: 1.4,
            borderRadius: 2,
            backgroundColor: pageColors.panelDark,
            border: `1px solid ${pageColors.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: pageColors.textMuted, fontWeight: 800 }}>
            {formatDate(activity.activity_date)} ・ {activity.author}
          </Typography>
          <Typography sx={{ color: pageColors.text, fontWeight: 800, mt: 0.45, lineHeight: 1.7 }}>
            {activity.issue}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
};

const MeetingsTab = ({ activities, loading }: ActivityTabProps) => {
  if (loading) return <DetailLoading label="打合せ記録を読み込み中..." />;

  const meetingActivities = activities.filter((activity) => {
    const source = normalizeText(`${activity.activity_type} ${activity.decision}`);
    return (
      source.includes("会議") ||
      source.includes("打合") ||
      source.includes("訪問") ||
      source.includes("決定") ||
      Boolean(activity.decision)
    );
  });

  if (meetingActivities.length === 0) {
    return (
      <EmptyState
        icon={<GroupsOutlinedIcon />}
        title="打合せ・決定事項がありません"
        description="打合せの内容、決まったこと、保留事項、判断者を記録します。"
      />
    );
  }

  return (
    <Stack spacing={1}>
      {meetingActivities.map((activity) => (
        <Paper
          key={activity.activity_id}
          elevation={0}
          sx={{
            p: 1.4,
            borderRadius: 2,
            backgroundColor: pageColors.panelDark,
            border: `1px solid ${pageColors.border}`,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.8}>
            <Box>
              <Typography variant="caption" sx={{ color: pageColors.textMuted, fontWeight: 800 }}>
                {formatDate(activity.activity_date)} ・ {activity.author}
              </Typography>
              <Typography sx={{ color: pageColors.text, fontWeight: 900, mt: 0.35 }}>
                {activity.subject || activity.activity_type || "打合せ記録"}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={activity.activity_type || "打合せ"}
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                color: pageColors.blue,
                backgroundColor: "rgba(109,168,255,0.10)",
                border: "1px solid rgba(109,168,255,0.26)",
                fontWeight: 800,
              }}
            />
          </Stack>

          <Typography sx={{ color: pageColors.textSoft, mt: 1, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {activity.content || "内容未登録"}
          </Typography>

          {activity.decision && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1.5,
                backgroundColor: "rgba(197,231,99,0.08)",
                border: `1px solid ${pageColors.borderStrong}`,
              }}
            >
              <Typography variant="caption" sx={{ color: pageColors.accent, fontWeight: 900 }}>
                決定事項
              </Typography>
              <Typography sx={{ color: pageColors.text, mt: 0.3, lineHeight: 1.7 }}>
                {activity.decision}
              </Typography>
            </Box>
          )}
        </Paper>
      ))}
    </Stack>
  );
};

const DocumentsTab = ({ project }: ProjectTabProps) => {
  const website = normalizeUrl(project.company_website);

  return (
    <Stack spacing={1.5}>
      <EmptyState
        icon={<DescriptionOutlinedIcon />}
        title="案件資料をまとめる場所"
        description="仕様書、図面、写真、議事録、テスト結果などを案件単位で管理する想定です。"
      />

      {website && (
        <Paper
          elevation={0}
          sx={{
            p: 1.4,
            borderRadius: 2,
            backgroundColor: pageColors.panelDark,
            border: `1px solid ${pageColors.border}`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box>
              <Typography sx={{ color: pageColors.text, fontWeight: 900 }}>
                顧客Webサイト
              </Typography>
              <Typography variant="caption" sx={{ color: pageColors.textMuted }}>
                {website}
              </Typography>
            </Box>
            <IconButton
              component="a"
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: pageColors.accent }}
            >
              <OpenInNewRoundedIcon />
            </IconButton>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

const HistoryTab = ({ activities, loading }: ActivityTabProps) => {
  if (loading) return <DetailLoading label="変更履歴を読み込み中..." />;

  if (activities.length === 0) {
    return (
      <EmptyState
        icon={<HistoryOutlinedIcon />}
        title="履歴がありません"
        description="状況変更、担当変更、コメント、資料更新などを時系列で表示します。"
      />
    );
  }

  return (
    <Stack spacing={0}>
      {activities.map((activity, index) => (
        <Box key={activity.activity_id} sx={{ display: "flex", gap: 1.2 }}>
          <Stack alignItems="center" sx={{ width: 28, flexShrink: 0 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                mt: 0.7,
                borderRadius: "50%",
                backgroundColor: index === 0 ? pageColors.accent : pageColors.textMuted,
                boxShadow: index === 0 ? "0 0 0 4px rgba(197,231,99,0.12)" : "none",
              }}
            />
            {index < activities.length - 1 && (
              <Box sx={{ width: 1, flex: 1, minHeight: 54, backgroundColor: pageColors.border }} />
            )}
          </Stack>

          <Box sx={{ pb: 1.5, flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: pageColors.textMuted, fontWeight: 800 }}>
              {formatDate(activity.activity_date)} ・ {activity.author}
            </Typography>
            <Typography sx={{ color: pageColors.text, fontWeight: 900, mt: 0.25 }}>
              {activity.subject || activity.activity_type || "活動記録を更新"}
            </Typography>
            <Typography sx={{ color: pageColors.textSoft, mt: 0.35, lineHeight: 1.65 }}>
              {activity.content || activity.next_action || "内容未登録"}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

/* =========================================================
 小コンポーネント
========================================================= */

type DetailBlockProps = {
  label: string;
  value: string;
  subValue?: string;
  multiline?: boolean;
};

const DetailBlock = ({ label, value, subValue, multiline = false }: DetailBlockProps) => {
  return (
    <DetailPaper>
      <Typography variant="caption" sx={{ color: pageColors.textMuted, fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.45,
          color: pageColors.text,
          fontWeight: 800,
          lineHeight: 1.7,
          whiteSpace: multiline ? "pre-wrap" : "normal",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
      {subValue && (
        <Typography variant="caption" sx={{ mt: 0.4, color: pageColors.textMuted, display: "block" }}>
          {subValue}
        </Typography>
      )}
    </DetailPaper>
  );
};

type ActivityCardProps = {
  activity: ProjectActivityRow;
  index: number;
};

const ActivityCard = ({ activity, index }: ActivityCardProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.4,
        borderRadius: 2,
        backgroundColor: pageColors.panelDark,
        border: `1px solid ${pageColors.border}`,
      }}
    >
      <Stack spacing={0.9}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`記録 ${index + 1}`}
              sx={{
                color: pageColors.accent,
                backgroundColor: "rgba(197,231,99,0.12)",
                border: `1px solid ${pageColors.borderStrong}`,
                fontWeight: 800,
              }}
            />
            <Typography sx={{ color: pageColors.text, fontWeight: 900 }}>
              {formatDate(activity.activity_date)}
            </Typography>
            <Typography sx={{ color: pageColors.textMuted, fontSize: 13 }}>
              {activity.author}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={activity.activity_type || "活動記録"}
              sx={{
                color: pageColors.textSoft,
                backgroundColor: "rgba(255,255,255,0.06)",
                border: `1px solid ${pageColors.border}`,
                fontWeight: 700,
              }}
            />
            {activity.status && (
              <Chip
                size="small"
                label={activity.status}
                sx={{
                  color: pageColors.cyan,
                  backgroundColor: "rgba(0,191,197,0.09)",
                  border: "1px solid rgba(0,191,197,0.24)",
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>
        </Stack>

        {activity.subject && (
          <Typography sx={{ color: pageColors.text, fontWeight: 900 }}>
            {activity.subject}
          </Typography>
        )}

        <Typography sx={{ color: pageColors.textSoft, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
          {activity.content || "内容未登録"}
        </Typography>

        {activity.next_action && (
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: "rgba(197,231,99,0.07)",
              border: `1px solid ${pageColors.borderStrong}`,
            }}
          >
            <Stack direction="row" spacing={0.7} alignItems="flex-start">
              <ArrowForwardRoundedIcon sx={{ color: pageColors.accent, fontSize: 19, mt: 0.15 }} />
              <Box>
                <Typography variant="caption" sx={{ color: pageColors.accent, fontWeight: 900 }}>
                  次に行うこと
                </Typography>
                <Typography sx={{ color: pageColors.text, mt: 0.2, lineHeight: 1.65 }}>
                  {activity.next_action}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

type EmptyStateProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const EmptyState = ({ icon, title, description }: EmptyStateProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        py: 5,
        px: 2,
        borderRadius: 2,
        textAlign: "center",
        backgroundColor: pageColors.panelDark,
        border: `1px dashed ${pageColors.border}`,
      }}
    >
      <Box sx={{ color: pageColors.textMuted, "& .MuiSvgIcon-root": { fontSize: 42 } }}>
        {icon}
      </Box>
      <Typography sx={{ mt: 1, color: pageColors.text, fontWeight: 900 }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 0.5, color: pageColors.textMuted, lineHeight: 1.7 }}>
        {description}
      </Typography>
    </Paper>
  );
};

const DetailLoading = ({ label }: { label: string }) => {
  return (
    <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress size={26} />
      <Typography sx={{ ml: 1.5, color: pageColors.textSoft }}>{label}</Typography>
    </Box>
  );
};

export default ProjectManagementData;